import { jsx as _jsx } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useEffect, useMemo, useRef, useState } from "react";
import { DEFAULT_WIDGET_HEIGHT } from "./types";
import { chartDocument } from "./lib/chartDocument";
import { frameHeight } from "./lib/chartSize";
/** The element's width in px, measured again whenever it changes. */
function useWidth(ref) {
    var _a = useState(null), width = _a[0], setWidth = _a[1];
    useEffect(function () {
        var element = ref.current;
        if (!element)
            return;
        var measure = function () { return setWidth(element.getBoundingClientRect().width); };
        measure();
        if (typeof ResizeObserver === "undefined")
            return;
        var observer = new ResizeObserver(measure);
        observer.observe(element);
        return function () { return observer.disconnect(); };
    }, [ref]);
    return width;
}
/**
 * A rendered chart in a frame that runs no script and loads nothing: an empty
 * `sandbox` and a CSP that allows only inline styles and data: fonts and
 * images. The document is replaced only when it changes, so a poll that brings
 * the same chart does not reload the frame.
 *
 * The frame is as tall as the chart at the width it is drawn, read from the
 * sanitized SVG's `viewBox`, up to twice the widget height; past that the
 * chart scrolls in it. A chart with no `viewBox` gets the widget height.
 */
export function MonitoringChartFrame(_a) {
    var _b;
    var svg = _a.svg, title = _a.title, _c = _a.height, height = _c === void 0 ? DEFAULT_WIDGET_HEIGHT : _c, colorScheme = _a.colorScheme, fontCss = _a.fontCss;
    var shown = useRef(null);
    var next = useMemo(function () { return chartDocument({ svg: svg, colorScheme: colorScheme, fontCss: fontCss }); }, [svg, colorScheme, fontCss]);
    if (((_b = shown.current) === null || _b === void 0 ? void 0 : _b.hash) !== next.hash)
        shown.current = next;
    var element = useRef(null);
    var width = useWidth(element);
    return (_jsx("iframe", { ref: element, sandbox: "", srcDoc: shown.current.html, title: title, "data-chart-hash": shown.current.hash, style: {
            display: "block",
            width: "100%",
            height: frameHeight(shown.current.size, width, height),
            border: 0,
        } }));
}
